import { useState } from 'react';
import { Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, SOFT_BADGE_CLASSES, useToast } from '@mustauto/ui/web';
import {
  CLAIM_NUMBER_MAX,
  INSURER_NAME_MAX,
  INSURER_OPTIONS,
  INSURER_RECEIVABLE_STATUS_LABELS,
  insurerNeedsName,
  isInsurerKey,
  type InsurerReceivableStatus,
} from '@mustauto/types';
import { formatApiError, formatDateBR, formatReais } from '@mustauto/utils';
import type { InsuranceClaim, InsuranceClaimInput, InsurerPaymentInput } from '@/hooks/useServiceOrders';

const inputClass = 'block h-8 w-full rounded-md border border-input bg-background px-2 text-sm';

const STATUS_BADGE: Record<InsurerReceivableStatus, string> = {
  nothing_due: SOFT_BADGE_CLASSES.gray,
  awaiting_service: SOFT_BADGE_CLASSES.slate,
  open: SOFT_BADGE_CLASSES.indigo,
  partial: SOFT_BADGE_CLASSES.amber,
  overdue: SOFT_BADGE_CLASSES.red,
  paid: SOFT_BADGE_CLASSES.emerald,
};

const METHOD_LABELS: Record<string, string> = {
  pix: 'Pix',
  boleto: 'Boleto',
  bank_transfer: 'Transferência',
  cash: 'Dinheiro',
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  other: 'Outro',
};

/** Hoje, no fuso do navegador — é o dia de quem está lançando. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Sinistro de seguradora no detalhe da OS (Fase 4 — lado da oficina).
 *
 * Decisões do dono do produto (24/09): o cliente paga a franquia e os itens que
 * a seguradora recusou, e é isso que fecha a OS; a seguradora paga o resto num
 * título à parte, com vencimento (o combinado, ou 30 dias da conclusão). Quem é
 * recusado se marca nos itens da OS, na edição de itens.
 *
 * Só para quem vê o financeiro — a API também exige.
 */
export function InsuranceClaimCard({
  claim,
  canEdit,
  suggest,
  onSave,
  onRemove,
  onAddPayment,
  onRemovePayment,
  saving,
}: {
  claim: InsuranceClaim | null;
  canEdit: boolean;
  /** Oferecer o registro mesmo sem sinistro (OS de funilaria). */
  suggest: boolean;
  onSave: (claim: InsuranceClaimInput) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  onAddPayment: (payment: InsurerPaymentInput) => Promise<unknown>;
  onRemovePayment: (paymentId: string) => Promise<unknown>;
  saving: boolean;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);

  const run = async (write: () => Promise<unknown>, done?: () => void) => {
    try {
      await write();
      done?.();
    } catch (err) {
      toast({ type: 'error', title: 'Não foi possível salvar', description: formatApiError(err, 'Tente novamente em instantes.') });
    }
  };

  if (!claim && !editing) {
    if (!canEdit || !suggest) return null;
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck size={16} className="text-muted-foreground" aria-hidden />
            <span>É sinistro de seguradora ou proteção veicular?</span>
          </div>
          <button type="button" onClick={() => setEditing(true)} className="text-sm font-medium text-primary hover:underline">
            Registrar sinistro
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck size={16} aria-hidden />
          Sinistro
        </CardTitle>
        {claim && !editing && canEdit && (
          <button type="button" onClick={() => setEditing(true)} className="text-sm text-primary hover:underline">
            Editar
          </button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {editing ? (
          <ClaimForm
            claim={claim}
            saving={saving}
            onCancel={() => setEditing(false)}
            onSave={(input) => run(() => onSave(input), () => setEditing(false))}
            onRemove={claim ? () => run(onRemove, () => setEditing(false)) : undefined}
          />
        ) : claim ? (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Seguradora" value={claim.insurerLabel} />
              <Field label="Nº do sinistro" value={claim.claimNumber ?? '—'} mono />
              <Field
                label="Autorização"
                value={claim.authorizedAt ? `Autorizado em ${new Date(claim.authorizedAt).toLocaleDateString('pt-BR')}` : 'Aguardando autorização'}
              />
            </div>
            <div className="rounded-lg border">
              <Row label="Coberto pela seguradora" value={formatReais(claim.covered)} />
              <Row label="Franquia (paga pelo cliente)" value={formatReais(claim.deductibleApplied)} />
              {claim.declined > 0 && <Row label="Itens recusados (pagos pelo cliente)" value={formatReais(claim.declined)} />}
              <Row label="A seguradora deve" value={formatReais(claim.insurerDue)} strong />
              {claim.insurerPaid > 0 && <Row label="Já pagou" value={formatReais(claim.insurerPaid)} />}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[claim.insurerStatus]}`}>
                {INSURER_RECEIVABLE_STATUS_LABELS[claim.insurerStatus]}
              </span>
              {claim.insurerRemaining > 0 && (
                <span className="font-medium">Falta {formatReais(claim.insurerRemaining)}</span>
              )}
              {claim.effectiveDueDate && (
                <span className="text-muted-foreground">
                  · vence {formatDateBR(claim.effectiveDueDate)}
                  {!claim.dueDate && ' (30 dias da conclusão)'}
                </span>
              )}
            </div>
            {claim.payments.length > 0 && (
              <ul className="divide-y rounded-lg border text-sm">
                {claim.payments.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="flex-1">
                      {formatDateBR(p.paidAt)} · {formatReais(p.amount)}
                      {p.method ? ` · ${METHOD_LABELS[p.method] ?? p.method}` : ''}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        aria-label="Remover pagamento da seguradora"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => void run(() => onRemovePayment(p.id))}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canEdit && claim.insurerRemaining > 0 &&
              (paying ? (
                <PaymentForm
                  remaining={claim.insurerRemaining}
                  saving={saving}
                  onCancel={() => setPaying(false)}
                  onSave={(payment) => run(() => onAddPayment(payment), () => setPaying(false))}
                />
              ) : (
                <button type="button" onClick={() => setPaying(true)} className="text-sm font-medium text-primary hover:underline">
                  Registrar pagamento da seguradora
                </button>
              ))}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-medium ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between border-t px-3 py-2 text-sm first:border-t-0">
      <span className={strong ? 'font-semibold' : 'text-muted-foreground'}>{label}</span>
      <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );
}

function ClaimForm({
  claim,
  saving,
  onCancel,
  onSave,
  onRemove,
}: {
  claim: InsuranceClaim | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (input: InsuranceClaimInput) => Promise<void>;
  onRemove?: () => Promise<void>;
}) {
  const [insurer, setInsurer] = useState(claim?.insurer ?? 'porto');
  const [insurerName, setInsurerName] = useState(claim?.insurerName ?? '');
  const [claimNumber, setClaimNumber] = useState(claim?.claimNumber ?? '');
  const [deductible, setDeductible] = useState(claim ? String(claim.deductible) : '');
  const [authorized, setAuthorized] = useState(!!claim?.authorizedAt);
  const [dueDate, setDueDate] = useState(claim?.dueDate ?? '');
  const [notes, setNotes] = useState(claim?.notes ?? '');

  const needsName = isInsurerKey(insurer) && insurerNeedsName(insurer);
  const deductibleValue = Number(deductible.replace(',', '.'));
  const valid = Number.isFinite(deductibleValue) && deductibleValue >= 0 && (!needsName || insurerName.trim() !== '');

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        void onSave({
          insurer,
          insurerName: needsName ? insurerName.trim() : null,
          claimNumber: claimNumber.trim() || null,
          deductible: deductibleValue,
          // Mantém a data original da autorização ao editar outra coisa.
          authorizedAt: authorized ? (claim?.authorizedAt ?? new Date().toISOString()) : null,
          dueDate: dueDate || null,
          notes: notes.trim() || null,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Seguradora</span>
          <select id="claim-insurer" className={inputClass} value={insurer} onChange={(e) => setInsurer(e.target.value)}>
            {INSURER_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {needsName && (
          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">
              {insurer === 'protecao_veicular' ? 'Nome da associação' : 'Nome da seguradora'}
            </span>
            <input id="claim-insurer-name" className={inputClass} value={insurerName} maxLength={INSURER_NAME_MAX} onChange={(e) => setInsurerName(e.target.value)} />
          </label>
        )}
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Nº do sinistro</span>
          <input id="claim-number" className={`${inputClass} font-mono`} value={claimNumber} maxLength={CLAIM_NUMBER_MAX} onChange={(e) => setClaimNumber(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">
            {insurer === 'protecao_veicular' ? 'Cota de participação (R$)' : 'Franquia (R$)'}
          </span>
          <input id="claim-deductible" className={inputClass} inputMode="decimal" value={deductible} onChange={(e) => setDeductible(e.target.value)} placeholder="1500,00" />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Vencimento combinado (opcional)</span>
          <input id="claim-due" type="date" className={inputClass} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 self-end pb-1.5 text-sm">
          <input id="claim-authorized" type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} className="rounded border-input" />
          A seguradora já autorizou o conserto
        </label>
      </div>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Observações (só da oficina)</span>
        <input id="claim-notes" className={inputClass} value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} placeholder="Regulador, vistoria, contato…" />
      </label>
      <p className="text-xs text-muted-foreground">
        Sem vencimento combinado, a seguradora vence 30 dias depois da conclusão da OS. Os itens que ela recusar se marcam na
        edição dos itens da OS.
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        {onRemove ? (
          <button type="button" onClick={() => void onRemove()} className="text-sm text-muted-foreground hover:text-destructive" disabled={saving}>
            Não é mais sinistro
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!valid || saving}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
          >
            {saving && <Loader2 size={14} className="animate-spin" aria-hidden />}
            Salvar sinistro
          </button>
        </div>
      </div>
    </form>
  );
}

function PaymentForm({
  remaining,
  saving,
  onCancel,
  onSave,
}: {
  remaining: number;
  saving: boolean;
  onCancel: () => void;
  onSave: (payment: InsurerPaymentInput) => Promise<void>;
}) {
  const [amount, setAmount] = useState(String(remaining));
  const [paidAt, setPaidAt] = useState(todayIso());
  const [method, setMethod] = useState('bank_transfer');
  const value = Number(amount.replace(',', '.'));
  const valid = Number.isFinite(value) && value > 0 && value <= remaining && /^\d{4}-\d{2}-\d{2}$/.test(paidAt);

  return (
    <form
      className="space-y-3 rounded-lg border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) void onSave({ amount: value, paidAt, method, notes: null });
      }}
    >
      <p className="text-xs font-medium text-muted-foreground">Pagamento da seguradora</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Valor (R$)</span>
          <input id="insurer-pay-amount" className={inputClass} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Data</span>
          <input id="insurer-pay-date" type="date" className={inputClass} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Forma</span>
          <select id="insurer-pay-method" className={inputClass} value={method} onChange={(e) => setMethod(e.target.value)}>
            {Object.entries(METHOD_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {value > remaining && <p className="text-xs text-destructive">A seguradora ainda deve {formatReais(remaining)}.</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!valid || saving}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {saving && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Registrar
        </button>
      </div>
    </form>
  );
}
